import { Injectable } from '@angular/core';
import {
  AngularFirestore,
  DocumentReference,
  DocumentSnapshot,
  QuerySnapshot,
} from '@angular/fire/compat/firestore';
import { Camp, StepUrlMapping } from '../modals/camp';
import { CampRegistration } from '../modals/campRegistration';
import { Participant } from '../modals/participant';
import * as moment from 'moment-timezone';
import { Transaction } from '../modals/transactions';
import { MailTemplate } from '../modals/mailTemplate';
import { Observable, Observer, Subscription } from 'rxjs';
import { DragAndDropTable } from 'src/app/components/helpers/drag-and-drop/drag-and-drop';
import { RegistrationCompleteStep } from '../modals/registrationCompleteStep/registartionCompleteStep';

@Injectable({
  providedIn: 'root',
})
export class StoreService {
  constructor(private readonly store: AngularFirestore) {}

  getParticipantById(participantId: string): Promise<Participant | undefined> {
    return new Promise<Participant | undefined>((resolve, reject) => {
      return this.store
        .doc<Participant>('/participant/' + participantId)
        .valueChanges()
        .subscribe(
          (participant) =>
            resolve({
              ...participant,
              id: participantId,
            } as any as Participant),
          (error) => reject(error)
        );
    });
  }

  getParticipantByIdObs(
    participantId: string
  ): Observable<Participant | undefined> {
    const docRef = this.store.doc<Participant>('/participant/' + participantId);

    return new Observable<Participant | undefined>((observer) => {
      const subscription = docRef.valueChanges().subscribe({
        next: (participant: any) => {
          if (!participant) {
            observer.next(participant);
            return;
          }
          observer.next({
            ...participant,
            id: participantId,
          } as any as Participant);
        },
        error: (error) => {
          observer.error(error);
        },
        complete: () => {
          subscription.unsubscribe();
        },
      });

      return new Subscription(() => {
        subscription.unsubscribe();
      });
    });
  }

  getCampRegistrationById(campRegistrationId: string) {
    return new Promise<CampRegistration | undefined>((resolve) => {
      this.store
        .doc<CampRegistration>('/campRegistrations/' + campRegistrationId)
        .valueChanges()
        .subscribe((campRegistration: any) => {
          if (!campRegistration) {
            return campRegistration;
          }
          const mergeCampRegistration = campRegistration as any;
          mergeCampRegistration.departureDate = this.toDateTime(
            campRegistration.departureDate
          );
          mergeCampRegistration.arivalDate = this.toDateTime(
            campRegistration.arivalDate
          );
          resolve(mergeCampRegistration as CampRegistration);
        });
    });
  }

  getCampRegistrationByIdObs(
    campRegistrationId: string
  ): Observable<CampRegistration | undefined> {
    const docRef = this.store.doc<CampRegistration>(
      '/campRegistrations/' + campRegistrationId
    );

    return new Observable<CampRegistration | undefined>((observer) => {
      const subscription = docRef.valueChanges().subscribe({
        next: (campRegistration: any) => {
          if (!campRegistration) {
            observer.next(campRegistration);
            return;
          }

          const mergeCampRegistration = campRegistration as any;
          mergeCampRegistration.departureDate = this.toDateTime(
            campRegistration.departureDate
          );
          mergeCampRegistration.arivalDate = this.toDateTime(
            campRegistration.arivalDate
          );
          observer.next(mergeCampRegistration as CampRegistration);
        },
        error: (error) => {
          observer.error(error);
        },
        complete: () => {
          subscription.unsubscribe();
        },
      });

      return new Subscription(() => {
        subscription.unsubscribe();
      });
    });
  }

  getCampById(
    campId: string,
    loadReferences?: boolean
  ): Promise<Camp | undefined> {
    return new Promise<Camp | undefined>((resolve) => {
      this.store
        .doc('/camps/' + campId)
        .get()
        .subscribe(async (camp) => {
          if (!camp.exists) {
            resolve(undefined);
            return;
          }

          const campMerged = camp.data() as any;
          campMerged.startDate = this.toDateTime(campMerged.startDate);
          campMerged.endDate = this.toDateTime(campMerged.endDate);

          const startDate = moment(campMerged.startDate);
          const endDate = moment(campMerged.endDate);
          campMerged.startDateFormatted = startDate.format('DD.MM.YYYY');
          campMerged.startTimeFormatted =
            startDate.format('DD.MM.YYYY') +
            '&nbsp;' +
            $localize`at` +
            '&nbsp;' +
            startDate.format('HH:mm');
          campMerged.endDateFormatted = endDate.format('DD.MM.YYYY');
          campMerged.endTimeFormatted =
            endDate.format('DD.MM.YYYY') +
            '&nbsp;' +
            $localize`at` +
            '&nbsp;' +
            endDate.format('HH:mm');

          if (loadReferences) {
            const stepReferences = await Promise.all(
              campMerged.registrationCompleteSteps.map(
                async (step: StepUrlMapping) => await step.stepId.get()
              )
            );

            const stepsWithData = await Promise.all(
              campMerged.registrationCompleteSteps.map(
                async (step: StepUrlMapping) => {
                  const stepReference = stepReferences.find(
                    (stepReference) => stepReference.id === step.stepId.id
                  );
                  if (!stepReference) {
                    return null;
                  }

                  const data = await stepReference.data();
                  return { ...data, url: step.url, id: stepReference.id };
                }
              )
            );

            campMerged.registrationCompleteSteps = stepsWithData;
          }

          resolve(campMerged as Camp);
        });
    });
  }

  getAllCamps(): Observable<Camp[]> {
    return new Observable<Camp[]>((observer: Observer<Camp[]>) => {
      const collectionRef = this.store.collection('/camps');

      const subscription = collectionRef
        .valueChanges({ idField: 'campId' })
        .subscribe({
          next: (camps: any[]) => {
            const returnCamps = camps
              .map((camp) => {
                const campMerged = camp as any;
                campMerged.startDate = this.toDateTime(campMerged.startDate);
                campMerged.endDate = this.toDateTime(campMerged.endDate);
                campMerged.id = campMerged.campId;
                return campMerged;
              })
              .sort((a, b) => b.startDate - a.startDate);

            observer.next(returnCamps as Camp[]);
          },
          error: (error) => {
            observer.error(error);
          },
          complete: () => {
            observer.complete();
            subscription.unsubscribe(); // Unsubscribe when the observable completes
          },
        });

      // Returning the subscription so that the caller can unsubscribe if needed
      return new Subscription(() => {
        subscription.unsubscribe();
      });
    });
  }

  toDateTime(date: any) {
    if (!date) {
      return null;
    }
    if (!date.toDate) {
      return date;
    }
    const newDate = moment(date.toDate()).tz('Europe/Berlin').toDate();
    return newDate;
  }

  async addMail(to: string[], template: string, data: any) {
    return this.store.collection('mail').add({
      to: to,
      template: {
        name: template,
        data,
      },
    });
  }

  getCampRegistraionsByCampId(
    campId: string
  ): Observable<CampRegistration[] | undefined> {
    return new Observable<CampRegistration[] | undefined>(
      (observer: Observer<CampRegistration[] | undefined>) => {
        this.store
          .collection<CampRegistration>('/campRegistrations')
          .ref.where('campId', '==', campId)
          .onSnapshot((querySnapshot: QuerySnapshot<CampRegistration>) => {
            const data: CampRegistration[] = [];

            querySnapshot.forEach((doc) => {
              const registrationMerge = doc.data() as any;
              registrationMerge.id = doc.id;
              registrationMerge.registrationDate = this.toDateTime(
                registrationMerge.registrationDate
              );
              registrationMerge.parentPermissionCompleteDate = this.toDateTime(
                registrationMerge.parentPermissionCompleteDate
              );
              registrationMerge.arivalDate = this.toDateTime(
                registrationMerge.arivalDate
              );
              registrationMerge.departureDate = this.toDateTime(
                registrationMerge.departureDate
              );
              registrationMerge.transactions =
                registrationMerge.transactions.map(
                  (transaction: Transaction) => {
                    transaction.date = this.toDateTime(
                      transaction.date
                    ) as Date;
                    return transaction;
                  }
                );
              data.push(registrationMerge as CampRegistration);
            });

            observer.next(data);
          });
      }
    );
  }

  async getParticipantsByIDs(
    participantIds: string[]
  ): Promise<Participant[] | undefined> {
    return new Promise<Participant[] | undefined>(async (resolve) => {
      const querries = participantIds.map((participant) =>
        this.getParticipantById(participant)
      );

      const data = await Promise.all(querries);

      resolve(data as Participant[]);
    });
  }

  updateRegistration(registration: CampRegistration) {
    return this.store
      .doc('/campRegistrations/' + registration.id)
      .update(registration);
  }

  updateCamp(camp: Camp) {
    return this.store.doc('/camps/' + camp.campId).update(camp);
  }

  updateParticipant(participant: Participant) {
    return this.store.doc('/participant/' + participant.id).update(participant);
  }

  getAllFailedEmails(): Promise<any> {
    return new Promise<any[] | undefined>(async (resolve) => {
      const data: any[] = [];
      await this.store
        .collection<any>('/mail')
        .ref.where('delivery.state', '!=', 'SUCCESS')
        .get()
        .then((querySnapshot: QuerySnapshot<any>) => {
          querySnapshot.forEach((doc) => {
            const registrationMerge = doc.data() as any;
            registrationMerge.id = doc.id;
            registrationMerge.delivery.startTime = this.toDateTime(
              registrationMerge.delivery.startTime
            );
            registrationMerge.delivery.endTime = this.toDateTime(
              registrationMerge.delivery.endTime
            );
            data.push(registrationMerge as CampRegistration);
          });
          resolve(data);
        });
    });
  }

  getAllEmailyByParticipant(email: string): Promise<any> {
    return new Promise<any[] | undefined>(async (resolve) => {
      const data: any[] = [];
      await this.store
        .collection<any>('/mail')
        .ref.where('template.data.participant.email', '==', email)
        .get()
        .then((querySnapshot: QuerySnapshot<any>) => {
          querySnapshot.forEach((doc) => {
            const registrationMerge = doc.data() as any;
            registrationMerge.id = doc.id;
            registrationMerge.delivery.startTime = this.toDateTime(
              registrationMerge.delivery.startTime
            );
            registrationMerge.delivery.endTime = this.toDateTime(
              registrationMerge.delivery.endTime
            );
            data.push(registrationMerge as CampRegistration);
          });
          resolve(data);
        });
    });
  }

  getAllMailTemplates(): Observable<MailTemplate[]> {
    return this.store
      .collection('/templates')
      .valueChanges({ idField: 'id' }) as any as Observable<MailTemplate[]>;
  }

  getMailTemplateById(
    templateId: string
  ): Observable<DocumentSnapshot<MailTemplate>> {
    return this.store.doc('/templates/' + templateId).get() as Observable<
      DocumentSnapshot<MailTemplate>
    >;
  }

  updateMailTemplate(mailTemplate: MailTemplate) {
    return this.store.doc('/templates/' + mailTemplate.id).update(mailTemplate);
  }

  getDragAndDropTableById(
    dragAndDropTableId: string
  ): Observable<DocumentSnapshot<DragAndDropTable>> {
    return this.store
      .doc('/drag-and-drop/' + dragAndDropTableId)
      .get() as Observable<DocumentSnapshot<DragAndDropTable>>;
  }

  updateDragAndDropTable(dragAndDropTable: DragAndDropTable) {
    return this.store
      .doc('/drag-and-drop/' + dragAndDropTable.id)
      .update(dragAndDropTable);
  }

  getSignatureById(signatureId: string) {
    return this.store.doc('/signatures/' + signatureId).get();
  }

  getStepById(
    stepId: string
  ): Observable<DocumentSnapshot<RegistrationCompleteStep>> {
    return this.store
      .doc<RegistrationCompleteStep>('/registration-complete-steps/' + stepId)
      .get() as Observable<DocumentSnapshot<RegistrationCompleteStep>>;
  }

  updateStep(step: RegistrationCompleteStep) {
    const newStep = { ...step } as any;
    delete newStep.id;
    return this.store
      .doc('/registration-complete-steps/' + step.id)
      .update(newStep);
  }
}
